import {
  type ExpandedState,
  type OnChangeFn,
  type Row,
  type SortingState,
  getCoreRowModel,
  getExpandedRowModel,
} from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import type { CombinedError } from "urql"

import { useSalesOpportunityColumns } from "../../hooks/useSalesOpportunityColumns"

import { SalesOpportunityExpandedRow } from "./SalesOpportunityExpandedRow"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { SalesPipeline } from "@/graphql/codegen/graphql"

interface SalesOpportunityTableProps {
  data: SalesPipeline[]
  loading?: boolean
  error?: CombinedError | Error
  sorting: SortingState
  expandedState: ExpandedState
  containerRef?: RefObject<HTMLDivElement>
  onSortingChange: OnChangeFn<SortingState>
  onExpandedChange: OnChangeFn<ExpandedState>
  onScroll?: () => void
}

export const SalesOpportunityTable: FC<SalesOpportunityTableProps> = ({
  data,
  error,
  loading = false,
  sorting,
  expandedState,
  containerRef,
  onSortingChange,
  onExpandedChange,
  onScroll,
}) => {
  const columns = useSalesOpportunityColumns()

  const renderExpandedContent = (row: Row<SalesPipeline>) => <SalesOpportunityExpandedRow row={row} />

  return (
    <AerosBaseTable<SalesPipeline>
      error={error}
      features={{
        table: { stickyHeader: true },
        tanstackOptions: {
          data,
          columns,
          state: { sorting, isLoading: loading, expanded: expandedState },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          getRowId: (row) => row.id,
          onSortingChange,
          onExpandedChange,
          getRowCanExpand: () => true,
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: { ref: containerRef, onScroll },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No opportunities found'
    />
  )
}
